import { ReactNode } from "react";
import { Box, SxProps, Theme, Typography } from "@mui/material";
import { FORM_STATUS_ENUM } from "@interfaces/response";
import { ChipEstadoExpediente } from "./ChipEstadoExpediente";
import { ButtonStartExpediente } from "./ButtonStartExpediente";

interface Props {
  /** Encabezado de la sección y estado que se muestra en el chip. */
  title: string;
  status: FORM_STATUS_ENUM;
  /** Texto secundario bajo el título (opcional). */
  subtitle?: ReactNode;
  /** Icono centrado en el cuadro de color de marca. */
  icon: ReactNode;
  /**
   * Texto o nodo informativo al centro de la franja. Admite `<strong>` para resaltar datos;
   * el componente ya aplica `color: primary.dark / fontWeight: 600` sobre ellos.
   */
  description?: ReactNode;
  /** Etiqueta del botón de acción. */
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Requerido por la convención del repo. */
  "data-testid": string;
  /** Overrides sobre el contenedor externo. */
  sx?: SxProps<Theme>;
}

/**
 * Sección de proceso guiado del expediente: encabezado con título y estado (`.proc-title`),
 * subtítulo opcional y la franja de acción (`.proc-row`) con icono, dato y CTA.
 *
 * La forma es fija a propósito — todas las secciones del expediente deben verse igual.
 */
export const ExpProcessRow = ({
  title,
  status,
  subtitle,
  icon,
  description,
  label,
  onClick,
  disabled,
  sx,
  ...rest
}: Props) => (
  <Box sx={sx}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
      <Typography variant="body1" sx={{ fontWeight: 600, letterSpacing: "-0.015em", color: "primary.dark" }}>
        {title}
      </Typography>
      <ChipEstadoExpediente formStatus={status} />
    </Box>

    {subtitle && (
      <Typography variant="body2" sx={{ color: "text.disabled", lineHeight: 1.55, mt: "4px" }}>
        {subtitle}
      </Typography>
    )}

    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        p: "10px 12px 10px 14px",
        mt: "14px",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "10px",
        bgcolor: "tokens.surface2",
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "8px",
          bgcolor: "primary.base",
          color: "primary.main",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Typography
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          color: "text.secondary",
          "& strong": { color: "primary.dark", fontWeight: 600 },
        }}
      >
        {description}
      </Typography>

      <ButtonStartExpediente onClick={onClick} disabled={disabled} {...rest}>
        {label}
      </ButtonStartExpediente>
    </Box>
  </Box>
);
